/**
 * src/utils/crypto.js
 * 
 * Enterprise-grade E2EE Utility using Web Crypto API.
 * 
 * Flow:
 *  1. Identity Keypair: ECDH (P-256) generated once per user.
 *  2. Private Key Storage: Persistent in IndexedDB.
 *  3. Key Agreement: ECDH + HKDF (SHA-256) to derive a symmetric key.
 *  4. Message Encryption: AES-GCM (256-bit).
 *  5. Key Escrow: Private key is encrypted with PBKDF2(user_password) and uploaded to server.
 */

const DB_NAME = "chat_crypto_db";
const STORE_NAME = "keys";
const KEY_ALIAS = "identity_keypair";

// ── IndexedDB Helpers ────────────────────────────────────────────────────────

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE_NAME);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function saveKey(id, key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(key, id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function getKey(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(id);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ── Main Crypto Class ─────────────────────────────────────────────────────────

export class ChatCrypto {
  static arrayBufferToBase64(buffer) {
    return btoa(String.fromCharCode(...new Uint8Array(buffer)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "");
  }

  static base64ToArrayBuffer(base64) {
    const binaryString = atob(base64.replace(/-/g, "+").replace(/_/g, "/"));
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }
  /**
   * Generate or retrieve the user's identity keypair.
   */
  static async getIdentityKeyPair() {
    let pair = await getKey(KEY_ALIAS);
    if (!pair) {
      pair = await window.crypto.subtle.generateKey(
        { name: "ECDH", namedCurve: "P-256" },
        true, // extractable
        ["deriveKey", "deriveBits"]
      );
      await saveKey(KEY_ALIAS, pair);
    }
    return pair;
  }

  /**
   * Export public key to base64url string for sharing with others.
   */
  static async exportPublicKey(key) {
    const exported = await window.crypto.subtle.exportKey("spki", key);
    return ChatCrypto.arrayBufferToBase64(exported);
  }

  /**
   * Import a base64url public key from another user.
   */
  static async importPublicKey(base64) {
    const buffer = ChatCrypto.base64ToArrayBuffer(base64);
    return await window.crypto.subtle.importKey(
      "spki",
      buffer,
      { name: "ECDH", namedCurve: "P-256" },
      true,
      []
    );
  }

  /**
   * Derive a shared AES-GCM key from our private key and their public key.
   * Uses HKDF for key hardening.
   */
  static async deriveSharedKey(myPrivateKey, theirPublicKey, saltBase64) {
    const sharedSecret = await window.crypto.subtle.deriveBits(
      { name: "ECDH", public: theirPublicKey },
      myPrivateKey,
      256
    );

    const salt = saltBase64 ? ChatCrypto.base64ToArrayBuffer(saltBase64) : new Uint8Array(16);

    const baseKey = await window.crypto.subtle.importKey(
      "raw",
      sharedSecret,
      "HKDF",
      false,
      ["deriveKey"]
    );

    return await window.crypto.subtle.deriveKey(
      {
        name: "HKDF",
        salt: salt,
        info: new TextEncoder().encode("chat-message-encryption"),
        hash: "SHA-256",
      },
      baseKey,
      { name: "AES-GCM", length: 256 },
      true,
      ["encrypt", "decrypt"]
    );
  }

  /**
   * Encrypt a plaintext string using an AES-GCM key.
   * Returns { ciphertext: base64, iv: base64 }
   */
  static async encrypt(plaintext, aesKey) {
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const encoded = new TextEncoder().encode(plaintext);

    const encrypted = await window.crypto.subtle.encrypt(
      { name: "AES-GCM", iv: iv },
      aesKey,
      encoded
    );

    return {
      ciphertext: ChatCrypto.arrayBufferToBase64(encrypted),
      iv: ChatCrypto.arrayBufferToBase64(iv),
    };
  }

  /**
   * Decrypt a ciphertext using an AES-GCM key.
   */
  static async decrypt(ciphertextBase64, ivBase64, aesKey) {
    try {
      const ciphertext = ChatCrypto.base64ToArrayBuffer(ciphertextBase64);
      const iv = ChatCrypto.base64ToArrayBuffer(ivBase64);

      const decrypted = await window.crypto.subtle.decrypt(
        { name: "AES-GCM", iv: iv },
        aesKey,
        ciphertext
      );

      return new TextDecoder().decode(decrypted);
    } catch (err) {
      // Return null silently on decryption failure (e.g. key candidate trial or rotated key)
      return null;
    }
  }

  /**
   * Helper to derive a key from a password (PBKDF2) for key escrow.
   */
  static async deriveKeyFromPassword(password, salt) {
    const encoder = new TextEncoder();
    const baseKey = await window.crypto.subtle.importKey(
      "raw",
      encoder.encode(password),
      "PBKDF2",
      false,
      ["deriveKey"]
    );

    return await window.crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt: salt,
        iterations: 310000,
        hash: "SHA-256",
      },
      baseKey,
      { name: "AES-GCM", length: 256 },
      true,
      ["encrypt", "decrypt"]
    );
  }

  /**
   * Encrypt a file (Blob or File) using an AES-GCM conversation key.
   * We generate a fresh random file key, encrypt the file with it,
   * then encrypt the file key with the conversation key.
   */
  static async encryptFile(file, conversationKey) {
    // 1. Generate a random 256-bit key for the file itself
    const fileKey = await window.crypto.subtle.generateKey(
      { name: "AES-GCM", length: 256 },
      true,
      ["encrypt", "decrypt"]
    );

    // 2. Encrypt the file data
    const fileIv = window.crypto.getRandomValues(new Uint8Array(12));
    const fileBuffer = await file.arrayBuffer();
    const encryptedFileBuffer = await window.crypto.subtle.encrypt(
      { name: "AES-GCM", iv: fileIv },
      fileKey,
      fileBuffer
    );

    // 3. Export the file key and encrypt it with the conversation key
    const exportedFileKey = await window.crypto.subtle.exportKey("raw", fileKey);
    const fileKeyIv = window.crypto.getRandomValues(new Uint8Array(12));
    const encryptedFileKey = await window.crypto.subtle.encrypt(
      { name: "AES-GCM", iv: fileKeyIv },
      conversationKey,
      exportedFileKey
    );

    return {
      encryptedBlob: new Blob([encryptedFileBuffer], { type: "application/octet-stream" }),
      encryptedFileKey: ChatCrypto.arrayBufferToBase64(encryptedFileKey),
      fileKeyIv: ChatCrypto.arrayBufferToBase64(fileKeyIv),
      fileIv: ChatCrypto.arrayBufferToBase64(fileIv),
    };
  }

  /**
   * Decrypt a file using the encrypted file key and conversation key.
   */
  static async decryptFile(encryptedBuffer, encryptedFileKeyBase64, fileKeyIvBase64, fileIvBase64, conversationKey) {
    // 1. Decrypt the file key using the conversation key
    const encryptedFileKey = ChatCrypto.base64ToArrayBuffer(encryptedFileKeyBase64);
    const fileKeyIv = ChatCrypto.base64ToArrayBuffer(fileKeyIvBase64);
    
    const decryptedFileKeyBuffer = await window.crypto.subtle.decrypt(
      { name: "AES-GCM", iv: fileKeyIv },
      conversationKey,
      encryptedFileKey
    );

    // 2. Import the raw file key
    const fileKey = await window.crypto.subtle.importKey(
      "raw",
      decryptedFileKeyBuffer,
      "AES-GCM",
      true,
      ["decrypt"]
    );

    // 3. Decrypt the file data
    const fileIv = ChatCrypto.base64ToArrayBuffer(fileIvBase64);
    const decryptedFileBuffer = await window.crypto.subtle.decrypt(
      { name: "AES-GCM", iv: fileIv },
      fileKey,
      encryptedBuffer
    );

    return decryptedFileBuffer;
  }
}
